import React, { useEffect, useState } from "react";
import Breadcrumbs from "components/Common/Breadcrumb";
import axiosInstance from "Axiosinstance";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

const dayOptions = [
  { label: "180 Days", value: 180 },
  { label: "90 Days", value: 90 },
];

const buildTagEntries = count =>
  Array.from({ length: count }, () => ({
    etagType: "",
    noOfDays: "",
  }));

const issueETagSchema = Yup.object().shape({
  firstName: Yup.string().trim().required("Please enter first name"),
  lastName: Yup.string().trim().required("Please enter last name"),
  email: Yup.string()
    .email("Please enter a valid email")
    .required("Please enter email"),
  phoneNumber: Yup.string()
    .trim()
    .required("Please enter phone number"),
  address: Yup.string().trim(),
  description: Yup.string().trim(),
  noOfETags: Yup.number()
    .typeError("Please enter number of E-Tags")
    .integer("Number of E-Tags must be a whole number")
    .min(1, "At least 1 E-Tag is required")
    .max(5, "You can issue a maximum of 5 E-Tags at a time")
    .required("Please enter number of E-Tags"),
  etags: Yup.array()
    .of(
      Yup.object().shape({
        etagType: Yup.string().required("Please select E-Tag type"),
        noOfDays: Yup.number()
          .oneOf([90, 180], "Please select valid number of days")
          .required("Please select number of days"),
      })
    )
    .min(1, "At least one E-Tag is required"),
});

const IssueETag = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [issuedETags, setIssuedETags] = useState([]);
  const [isLoadingIssuedETags, setIsLoadingIssuedETags] = useState(true);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const fetchIssuedETags = async () => {
    setIsLoadingIssuedETags(true);

    try {
      const { data } = await axiosInstance.get("/api/partner/issuedetag");
      setIssuedETags(data?.issuedETags || []);
    } catch (error) {
      setIssuedETags([]);
      toast.error(
        error?.response?.data?.message || "Unable to fetch issued E-Tags",
        {
          position: "top-center",
        }
      );
    } finally {
      setIsLoadingIssuedETags(false);
    }
  };

  useEffect(() => {
    fetchIssuedETags();
  }, []);

  const formatDate = value => {
    if (!value) {
      return "-";
    }

    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Partner Portal" breadcrumbItem="Issue E-Tag" />
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                  <div>
                    <h4 className="mb-1">Issue E-Tag</h4>
                    <p className="text-muted mb-0">
                      Create E-Bag or E-Passport requests for partner customers.
                    </p>
                  </div>
                  <Button color="primary" onClick={toggleModal}>
                    Issue E-Tag
                  </Button>
                </div>

                <div className="border rounded p-4 bg-light">
                  <p className="mb-2">Available issue types:</p>
                  <ul className="mb-0">
                    <li>E-Bag</li>
                    <li>E-Passport</li>
                  </ul>
                </div>

                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div>
                      <h5 className="mb-1">Issued E-Tags</h5>
                      <p className="text-muted mb-0">
                        All E-Tags issued by the logged-in partner.
                      </p>
                    </div>
                    <Button
                      color="light"
                      className="border"
                      onClick={fetchIssuedETags}
                      disabled={isLoadingIssuedETags}
                    >
                      Refresh
                    </Button>
                  </div>

                  {isLoadingIssuedETags ? (
                    <div className="text-center py-4 text-muted">
                      Loading issued E-Tags...
                    </div>
                  ) : issuedETags.length === 0 ? (
                    <div className="text-center py-4 border rounded text-muted bg-light">
                      No eTag issued
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>E-Tag Type</th>
                            <th>Tag Number</th>
                            <th>Email</th>
                            <th>No. of Days</th>
                            <th>Issued Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {issuedETags.map((item, index) => (
                            <tr key={item._id || `${item.tag_number}-${index}`}>
                              <td>{index + 1}</td>
                              <td>{item.etag_type || "-"}</td>
                              <td>{item.tag_number || "-"}</td>
                              <td>{item.email || "-"}</td>
                              <td>{item.no_of_days || "-"}</td>
                              <td>{formatDate(item.activation_date || item.create_date)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
          <ModalHeader toggle={toggleModal} tag="h4">
            Issue E-Tag
          </ModalHeader>
          <ModalBody>
            <Formik
              initialValues={{
                firstName: "",
                lastName: "",
                email: "",
                phoneNumber: "",
                address: "",
                description: "",
                noOfETags: 1,
                etags: buildTagEntries(1),
              }}
              validationSchema={issueETagSchema}
              onSubmit={async (values, { resetForm, setSubmitting }) => {
                try {
                  const payload = {
                    first_name: values.firstName.trim(),
                    last_name: values.lastName.trim(),
                    email: values.email,
                    phone_number: values.phoneNumber.trim(),
                    address: values.address.trim(),
                    description: values.description.trim(),
                    etags: values.etags.map(item => ({
                      type: item.etagType,
                      no_of_days: Number(item.noOfDays),
                    })),
                  };

                  const { data } = await axiosInstance.post(
                    "/api/issueetag",
                    payload
                  );

                  if (data?.status) {
                    toast.success(data?.message || "E-Tags issued successfully", {
                      position: "top-center",
                    });
                    await fetchIssuedETags();
                    resetForm({
                      values: {
                        firstName: "",
                        lastName: "",
                        email: "",
                        phoneNumber: "",
                        address: "",
                        description: "",
                        noOfETags: 1,
                        etags: buildTagEntries(1),
                      },
                    });
                    toggleModal();
                  } else {
                    toast.error(data?.message || "Unable to issue E-Tags", {
                      position: "top-center",
                    });
                  }
                } catch (error) {
                  toast.error(
                    error?.response?.data?.message ||
                      error?.response?.data?.msg ||
                      "Something went wrong",
                    {
                      position: "top-center",
                    }
                  );
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                errors,
                touched,
                values,
                isSubmitting,
                setFieldValue,
              }) => (
                <Form>
                  <div className="mb-3">
                    <Label className="form-label">First Name</Label>
                    <Field
                      name="firstName"
                      className={
                        "form-control" +
                        (errors.firstName && touched.firstName ? " is-invalid" : "")
                      }
                    />
                    <ErrorMessage
                      name="firstName"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="mb-3">
                    <Label className="form-label">Last Name</Label>
                    <Field
                      name="lastName"
                      className={
                        "form-control" +
                        (errors.lastName && touched.lastName ? " is-invalid" : "")
                      }
                    />
                    <ErrorMessage
                      name="lastName"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="mb-3">
                    <Label className="form-label">Email</Label>
                    <Field
                      name="email"
                      type="email"
                      className={
                        "form-control" +
                        (errors.email && touched.email ? " is-invalid" : "")
                      }
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="mb-3">
                    <Label className="form-label">Phone Number</Label>
                    <Field
                      name="phoneNumber"
                      className={
                        "form-control" +
                        (errors.phoneNumber && touched.phoneNumber
                          ? " is-invalid"
                          : "")
                      }
                    />
                    <ErrorMessage
                      name="phoneNumber"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="mb-3">
                    <Label className="form-label">Address</Label>
                    <Field
                      as="textarea"
                      name="address"
                      className={
                        "form-control" +
                        (errors.address && touched.address ? " is-invalid" : "")
                      }
                    />
                    <ErrorMessage
                      name="address"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="mb-3">
                    <Label className="form-label">Description</Label>
                    <Field
                      as="textarea"
                      name="description"
                      className={
                        "form-control" +
                        (errors.description && touched.description
                          ? " is-invalid"
                          : "")
                      }
                    />
                    <ErrorMessage
                      name="description"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="mb-3">
                    <Label className="form-label">No. of E-Tags</Label>
                    <Field
                      name="noOfETags"
                      type="number"
                      min="1"
                      max="5"
                      className={
                        "form-control" +
                        (errors.noOfETags && touched.noOfETags
                          ? " is-invalid"
                          : "")
                      }
                      onChange={event => {
                        const value = event.target.value;
                        setFieldValue("noOfETags", value);

                        const parsedValue = Number(value);

                        if (!Number.isInteger(parsedValue) || parsedValue < 1) {
                          setFieldValue("etags", []);
                          return;
                        }

                        const limitedCount = Math.min(parsedValue, 5);
                        const nextTags = Array.from(
                          { length: limitedCount },
                          (_, index) =>
                            values.etags[index] || {
                              etagType: "",
                              noOfDays: "",
                            }
                        );
                        setFieldValue("etags", nextTags);
                      }}
                    />
                    <ErrorMessage
                      name="noOfETags"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  {values.etags.length > 0 && (
                    <div className="border rounded p-3 bg-light mb-3">
                      <h5 className="mb-3">E-Tag Details</h5>
                      {values.etags.map((item, index) => (
                        <Row key={`etag-${index}`} className="mb-3">
                          <Col md="6">
                            <Label className="form-label">
                              Select Type {index + 1}
                            </Label>
                            <Field
                              as="select"
                              name={`etags.${index}.etagType`}
                              className={
                                "form-control" +
                                (errors.etags?.[index]?.etagType &&
                                touched.etags?.[index]?.etagType
                                  ? " is-invalid"
                                  : "")
                              }
                            >
                              <option value="">Select type</option>
                              <option value="ebag">E-Bag</option>
                              <option value="epassport">E-Passport</option>
                            </Field>
                            <ErrorMessage
                              name={`etags.${index}.etagType`}
                              component="div"
                              className="invalid-feedback"
                            />
                          </Col>
                          <Col md="6">
                            <Label className="form-label">
                              No. of Days {index + 1}
                            </Label>
                            <Field
                              as="select"
                              name={`etags.${index}.noOfDays`}
                              className={
                                "form-control" +
                                (errors.etags?.[index]?.noOfDays &&
                                touched.etags?.[index]?.noOfDays
                                  ? " is-invalid"
                                  : "")
                              }
                            >
                              <option value="">Select number of days</option>
                              {dayOptions.map(option => (
                                <option
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name={`etags.${index}.noOfDays`}
                              component="div"
                              className="invalid-feedback"
                            />
                          </Col>
                        </Row>
                      ))}
                    </div>
                  )}

                  <div className="text-end">
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      Submit
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default IssueETag;
